<template>
  <ion-page>
    <PageHeaderWithMenu title="Музыка" :scrolled="headerScrolled" />
    <ion-content
      :class="['music-library-content', isMobile ? 'main-background' : 'liga-web-background']"
      :fullscreen="true"
      @ionScroll="onContentScroll"
    >
      <ion-refresher slot="fixed" @ionRefresh="loadFolders($event)" :disabled="globalContext.offlineMode">
        <ion-refresher-content />
      </ion-refresher>
      <ion-list v-if="!loading && folders.length" class="music-library-list" lines="none">
        <ion-item
          v-for="f in folders"
          :key="f.id"
          button
          :detail="false"
          class="music-library-folder-item"
          @click="$router.push(`/music-library/${f.id}`)"
        >
          <ion-label>
            <h2>{{ f.title }}</h2>
            <p v-if="f.offlineSaved && !globalContext.offlineMode" class="music-library-offline-note">Сохранено офлайн</p>
            <p v-else-if="f.offlinePartial && !globalContext.offlineMode" class="music-library-offline-note music-library-offline-partial">Частично сохранено</p>
          </ion-label>
          <ion-icon slot="end" :icon="chevronForwardOutline" class="music-library-chevron" />
        </ion-item>
      </ion-list>
      <p v-else-if="!loading" class="ion-padding empty-hint music-library-empty">{{ emptyMessage }}</p>
      <ion-spinner v-if="loading" class="ion-margin music-library-spinner" name="crescent" />
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import { GlobalContext } from '@/model/globalcontext.model';
import { ILibraryMusicFolder, libraryMusicOfflineDirName, libraryMusicService } from '@/services/library-music.service';
import FilesystemCustomUtils from '@/services/filesystem.custom.utils';
import {
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
} from '@ionic/vue';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { isPlatform } from '@ionic/vue';
import { chevronForwardOutline } from 'ionicons/icons';
import { defineComponent, inject } from 'vue';

type FolderRow = ILibraryMusicFolder & { offlineSaved?: boolean; offlinePartial?: boolean };

export default defineComponent({
  name: 'MusicLibrary',
  components: {
    IonPage,
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    IonIcon,
    IonRefresher,
    IonRefresherContent,
    IonSpinner,
    PageHeaderWithMenu,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    const filesystemCustomUtils = new FilesystemCustomUtils();
    return {
      globalContext,
      filesystemCustomUtils,
      chevronForwardOutline,
    };
  },
  data() {
    return {
      folders: [] as FolderRow[],
      loading: true,
      headerScrolled: false,
    };
  },
  computed: {
    isMobile(): boolean {
      return this.globalContext.isMobile;
    },
    emptyMessage(): string {
      if (this.globalContext.offlineMode) {
        return 'Нет сохранённых треков. Сохраните треки в приложении при подключении к сети.';
      }
      return 'Нет доступных папок.';
    },
  },
  ionViewWillEnter() {
    this.loadFolders();
  },
  methods: {
    onContentScroll(ev: CustomEvent) {
      const t = (ev.detail as { scrollTop?: number }).scrollTop || 0;
      this.headerScrolled = t > 8;
    },

    async folderOfflineStatus(folderId: number): Promise<{ offlineSaved: boolean; offlinePartial: boolean }> {
      const dirName = libraryMusicOfflineDirName(folderId);
      const pl = await this.filesystemCustomUtils.loadLibraryMusicPlaylist(folderId);
      if (!pl?.tracks?.length) {
        return { offlineSaved: false, offlinePartial: false };
      }
      if (!pl.id) {
        pl.id = dirName;
      }
      const offlineTracks = await this.filesystemCustomUtils.filterOfflineTracks(
        this.filesystemCustomUtils.PLAYLISTS_DIRECTORY,
        pl
      );
      if (offlineTracks.length === 0) {
        return { offlineSaved: false, offlinePartial: false };
      }
      if (pl.expectedTrackCount && pl.expectedTrackCount > 0) {
        const allSaved = offlineTracks.length >= pl.expectedTrackCount;
        return { offlineSaved: allSaved, offlinePartial: !allSaved };
      }
      try {
        const apiTracks = await libraryMusicService.listUserTracks(folderId);
        const allSaved = offlineTracks.length >= apiTracks.length && apiTracks.length > 0;
        return { offlineSaved: allSaved, offlinePartial: !allSaved };
      } catch {
        return { offlineSaved: false, offlinePartial: true };
      }
    },

    async loadOfflineFoldersFromDisk(): Promise<ILibraryMusicFolder[]> {
      const out: ILibraryMusicFolder[] = [];
      const isNative = isPlatform('ios') || isPlatform('android');
      if (!isNative) return out;

      try {
        const res = await Filesystem.readdir({
          path: this.filesystemCustomUtils.PLAYLISTS_DIRECTORY,
          directory: Directory.Data,
        });
        for (const f of res.files) {
          const m = f.name.match(/^library_music_(\d+)$/);
          if (!m) continue;
          const id = parseInt(m[1], 10);
          const pl = await this.filesystemCustomUtils.loadLibraryMusicPlaylist(id);
          if (!pl?.tracks?.length) continue;
          if (!pl.id) pl.id = f.name;
          const offlineTracks = await this.filesystemCustomUtils.filterOfflineTracks(
            this.filesystemCustomUtils.PLAYLISTS_DIRECTORY,
            pl
          );
          if (offlineTracks.length === 0) continue;
          out.push({
            id,
            slug: `offline-${id}`,
            title: pl.name || `Папка ${id}`,
            sortOrder: id,
            accessMode: 'PUBLIC',
            allowedUserIds: [],
            offlineSaved: true,
          });
        }
      } catch {
        /* нет каталога playlists */
      }
      out.sort((a, b) => a.title.localeCompare(b.title, 'ru'));
      return out;
    },

    async loadFolders(ev?: CustomEvent) {
      this.loading = !ev;
      try {
        if (this.globalContext.offlineMode) {
          this.folders = await this.loadOfflineFoldersFromDisk();
        } else {
          const apiFolders = await libraryMusicService.listUserFolders();
          const isNative = isPlatform('ios') || isPlatform('android');
          const rows: FolderRow[] = await Promise.all(
            apiFolders.map(async (f) => {
              let offlineSaved = false;
              let offlinePartial = false;
              if (isNative) {
                const status = await this.folderOfflineStatus(f.id);
                offlineSaved = status.offlineSaved;
                offlinePartial = status.offlinePartial;
              }
              return { ...f, offlineSaved, offlinePartial } as FolderRow;
            })
          );
          this.folders = rows;
        }
      } catch (e) {
        console.error(e);
        this.folders = [];
      } finally {
        this.loading = false;
        if (ev && (ev.target as HTMLIonRefresherElement).complete) {
          (ev.target as HTMLIonRefresherElement).complete();
        }
      }
    },
  },
});
</script>

<style scoped>
.music-library-list {
  background: transparent !important;
  --background: transparent !important;
  padding: 12px 12px 24px;
}

.music-library-folder-item {
  --background: rgba(255, 255, 255, 0.06);
  --background-hover: rgba(255, 255, 255, 0.1);
  --background-activated: rgba(255, 255, 255, 0.12);
  --border-color: rgba(255, 255, 255, 0.1);
  --color: rgba(255, 255, 255, 0.95);
  --padding-start: 16px;
  --padding-end: 12px;
  --inner-padding-end: 4px;
  --min-height: 64px;
  margin-bottom: 10px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.music-library-folder-item::part(native) {
  border-radius: 14px;
}

.music-library-folder-item ion-label h2 {
  color: #fff !important;
  font-weight: 600;
  font-size: 1.05rem;
}

.music-library-offline-note {
  margin-top: 4px !important;
  font-size: 0.8rem !important;
  color: rgba(255, 255, 255, 0.5) !important;
}

.music-library-offline-partial {
  color: rgba(255, 200, 120, 0.75) !important;
}

.music-library-chevron {
  color: var(--ion-color-orange) !important;
  font-size: 1.25rem;
}

.music-library-empty {
  color: rgba(255, 255, 255, 0.55) !important;
  text-align: center;
}

.music-library-spinner {
  display: block;
  margin: 24px auto;
  color: var(--ion-color-orange);
  --color: var(--ion-color-orange);
}

@media (prefers-color-scheme: light) {
  .music-library-folder-item {
    --background: rgba(255, 255, 255, 0.1);
    --background-hover: rgba(255, 255, 255, 0.14);
    --background-activated: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.14);
  }
}
</style>
